/**
 * Add Cattle Page - Production-level cattle registration form
 * Updated to use master data IDs and proper field mappings for backend integration
 */

import { useState, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Save, ArrowLeft, Scan, Upload, Camera, FileText,
  User, Activity, Baby, Globe, Settings, Ruler,
  AlertCircle, CheckCircle, Clock, Plus, X
} from 'lucide-react';
import {
  gauShalaApi,
  calculateDobFromAge,
  type Breed,
  type Species,
  type Gender,
  type Color,
  type Cattle
} from '../../services/gaushala/api';

import { InputField, SelectField, TextAreaField, FileUploadField, MultiSelectField } from './CattleFormFields';
interface LanguageContextType {
  language: 'hi' | 'en';
  t: (key: string) => string;
}

interface AddCattleProps {
  languageContext: LanguageContextType;
}

export default function AddCattle({ languageContext }: AddCattleProps) {
  const navigate = useNavigate();

  // Validation constraints
  const VALIDATION_RULES = {
    uniqueAnimalId: { max: 20 },
    name: { max: 30 },
    rfidTagNo: { max: 20 },
    earTagNumber: { max: 15 },
    microchipNumber: { max: 20 },
    vaccinationStatus: { max: 100 },
    disability: { max: 100 },
    veterinarianName: { max: 50 },
    veterinarianContact: { max: 10 },
    dewormingSchedule: { max: 100 },
    medicalHistory: { max: 500 },
    previousOwner: { max: 50 },
    feedingSchedule: { max: 300 }
  };

  // Master data state
  const [breeds, setBreeds] = useState<Breed[]>([]);
  const [species, setSpecies] = useState<Species[]>([]);
  const [genders, setGenders] = useState<Gender[]>([]);
  const [colors, setColors] = useState<Color[]>([]);
  const [sheds, setSheds] = useState<any[]>([]);
  const [masterDataLoading, setMasterDataLoading] = useState(true);

  const [formData, setFormData] = useState({
    // Basic Identification - Updated to use IDs
    uniqueAnimalId: '',
    name: '',
    breedId: 0,
    speciesId: 0,
    genderId: 0,
    colorId: 0,
    ageYears: 0,  // User enters age, will be converted to dob on submit
    dateOfEntry: new Date().toISOString().split('T')[0],  // Store as date string for form input


    // Health & Medical Records
    vaccinationStatus: '',
    disability: '',
    veterinarianName: '',
    dewormingSchedule: '',
    lastHealthCheckup: '',
    veterinarianContact: '',
    medicalHistory: '',

    // Physical Characteristics - Updated field name
    weight: '',
    hornStatus: '',
    rfidTagNo: '',  // Changed from rfidTagNumber to match backend
    height: '',
    earTagNumber: '',
    microchipNumber: '',

    // Reproductive Details (Female only)
    milkingStatus: '',
    milkYieldPerDay: '',
    numberOfCalves: '',
    lactationNumber: '',
    lastCalvingDate: '',
    pregnancyStatus: '',

    // Origin & Ownership
    sourceOfAcquisition: '',
    previousOwner: '',
    dateOfAcquisition: '',
    ownershipStatus: '',

    // Shelter & Feeding
    shedNumber: [],
    typeOfFeed: [],
    feedingSchedule: '',

    // Supporting Documents
    photoFile: null as File | null,
    healthCertificate: null as File | null,
    vaccinationRecord: null as File | null,
    purchaseDocument: null as File | null
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning'; text: string } | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [activeSection, setActiveSection] = useState(0);

  // Load master data on component mount
  useEffect(() => {
    const loadMasterData = async () => {
      setMasterDataLoading(true);
      try {
        const [breedsRes, speciesRes, gendersRes, colorsRes, shedsRes] = await Promise.all([
          gauShalaApi.masterData.getAllBreeds(),
          gauShalaApi.masterData.getAllSpecies(),
          gauShalaApi.masterData.getAllGenders(),
          gauShalaApi.masterData.getAllColors(),
          gauShalaApi.sheds.getAvailableSheds(),
        ]);

        if (breedsRes.success && breedsRes.data) setBreeds(breedsRes.data);
        if (speciesRes.success && speciesRes.data) setSpecies(speciesRes.data);
        if (gendersRes.success && gendersRes.data) setGenders(gendersRes.data);
        if (colorsRes.success && colorsRes.data) setColors(colorsRes.data);
        if (shedsRes.success && shedsRes.data) setSheds(shedsRes.data);
      } catch (error) {
        console.error('Failed to load master data:', error);
        setMessage({
          type: 'error',
          text: 'Failed to load reference data. Please refresh the page.'
        });
      } finally {
        setMasterDataLoading(false);
      }
    };

    loadMasterData();
  }, []);

  const handleInputChange = useCallback((field: string, value: string | File | null) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error for this field - use functional update to prevent input unfocus bug
    setErrors(prev => {
      if (prev[field]) {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      }
      return prev;
    });
    setMessage(null);
  }, []); // Empty dependency array - function is now stable

  const handleScanRfid = async () => {
    setIsScanning(true);
    try {
      const response = await gauShalaApi.cattle.scanRfid();
      if (response.success && response.data) {
        handleInputChange('rfidTagNo', response.data.rfidTag);

        if (response.data.cattleInfo) {
          setMessage({
            type: 'warning',
            text: 'RFID tag already exists for another cattle'
          });
        } else {
          setMessage({
            type: 'success',
            text: 'RFID tag scanned successfully'
          });
        }
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Failed to scan RFID tag'
      });
    } finally {
      setIsScanning(false);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields validation - Updated for ID fields
    if (!formData.uniqueAnimalId.trim()) newErrors.uniqueAnimalId = 'Unique Animal ID is required';
    if (formData.breedId === 0) newErrors.breedId = 'Breed is required';
    if (formData.speciesId === 0) newErrors.speciesId = 'Species is required';
    if (formData.genderId === 0) newErrors.genderId = 'Gender is required';
    // Date of Entry is automatically set to current date, no validation needed
    if (!formData.rfidTagNo.trim()) newErrors.rfidTagNo = 'RFID Tag Number is required';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper function to convert date string (YYYY-MM-DD) to LocalDateTime format (YYYY-MM-DDTHH:MM:SS)
  const convertToLocalDateTime = (dateString: string): string | undefined => {
    if (!dateString || dateString.trim() === '') return undefined;
    // If already in ISO format, return as-is
    if (dateString.includes('T')) return dateString;
    // Convert date-only string to LocalDateTime at midnight
    return `${dateString}T00:00:00`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setMessage({
        type: 'error',
        text: 'Please fill in all required fields'
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      // Transform formData to match Cattle interface for backend - COMPLETE FIELD MAPPING
      // NOTE: gaushalaId is NOT sent - backend extracts it from JWT token automatically
      const cattleData: Omit<Cattle, 'id' | 'createdAt' | 'updatedAt' | 'gaushalaId'> = {
        uniqueAnimalId: formData.uniqueAnimalId,
        name: formData.name || undefined,
        breedId: formData.breedId,
        speciesId: formData.speciesId,
        genderId: formData.genderId,
        colorId: formData.colorId,
        dob: formData.ageYears > 0 ? calculateDobFromAge(formData.ageYears) : new Date().toISOString(),

        // Physical Characteristics
        weight: formData.weight ? parseFloat(formData.weight) : undefined,
        height: formData.height ? parseFloat(formData.height) : undefined,
        hornStatus: formData.hornStatus || undefined,
        rfidTagNo: formData.rfidTagNo,
        earTagNo: formData.earTagNumber || undefined,
        microchipNo: formData.microchipNumber || undefined,
        shedNumber: Array.isArray(formData.shedNumber) && formData.shedNumber.length > 0
          ? formData.shedNumber[0]
          : (typeof formData.shedNumber === 'string' ? formData.shedNumber : undefined),

        // Health & Medical Records
        vaccinationStatus: formData.vaccinationStatus || undefined,
        disability: formData.disability || undefined,
        dewormingSchedule: formData.dewormingSchedule || undefined,
        medicalHistory: formData.medicalHistory || undefined,
        lastHealthCheckupDate: formData.lastHealthCheckup ? convertToLocalDateTime(formData.lastHealthCheckup) : undefined,
        vetName: formData.veterinarianName || undefined,
        vetContact: formData.veterinarianContact || undefined,

        // Reproductive Details
        milkingStatus: formData.milkingStatus || undefined,
        lactationNumber: formData.lactationNumber ? parseInt(formData.lactationNumber) : undefined,
        milkYieldPerDay: formData.milkYieldPerDay ? parseFloat(formData.milkYieldPerDay) : undefined,
        lastCalvingDate: formData.lastCalvingDate ? convertToLocalDateTime(formData.lastCalvingDate) : undefined,
        calvesCount: formData.numberOfCalves ? parseInt(formData.numberOfCalves) : undefined,
        pregnancyStatus: formData.pregnancyStatus || undefined,

        // Origin & Ownership
        sourceId: formData.sourceOfAcquisition ? parseInt(formData.sourceOfAcquisition) : undefined,
        dateOfAcquisition: formData.dateOfAcquisition ? convertToLocalDateTime(formData.dateOfAcquisition) : undefined,
        previousOwner: formData.previousOwner || undefined,
        ownershipId: formData.ownershipStatus ? parseInt(formData.ownershipStatus) : undefined,

        // Shelter & Feeding
        feedingSchedule: formData.feedingSchedule || undefined,
        feedTypeId: Array.isArray(formData.typeOfFeed) && formData.typeOfFeed.length > 0
          ? parseInt(formData.typeOfFeed[0])
          : (formData.typeOfFeed ? parseInt(formData.typeOfFeed as string) : undefined),

        // System fields
        dateOfEntry: convertToLocalDateTime(new Date().toISOString().split('T')[0]),
        isActive: true,
        totalDungCollected: 0,
        lastDungCollection: 0,
      };

      const response = await gauShalaApi.cattle.createCattle(cattleData);

      console.log('🔵 [ADD CATTLE] Create response:', response);
      console.log('🔵 [ADD CATTLE] Cattle ID:', response.data?.id);

      if (response.success && response.data && response.data.id) {
        const cattleId = String(response.data.id); // Convert to string for API compatibility
        console.log('🔵 [ADD CATTLE] Uploading documents for cattle ID:', cattleId);

        // Upload documents if provided (non-blocking - don't fail registration if uploads fail)
        const uploadPromises = [];

        try {
          if (formData.photoFile) {
            console.log('🔵 [ADD CATTLE] Uploading photo...');
            uploadPromises.push(
              gauShalaApi.cattle.uploadPhoto(cattleId, formData.photoFile)
                .then(result => {
                  if (result.success) {
                    console.log('✅ Photo uploaded successfully');
                  } else {
                    console.warn('⚠️ Photo upload failed:', result.error);
                  }
                  return result;
                })
                .catch(err => {
                  console.error('❌ Photo upload error:', err);
                })
            );
          }

          if (formData.healthCertificate) {
            console.log('🔵 [ADD CATTLE] Uploading health certificate...');
            uploadPromises.push(
              gauShalaApi.cattle.uploadDocument(cattleId, formData.healthCertificate, 'health_certificate')
                .then(result => {
                  if (result.success) {
                    console.log('✅ Health certificate uploaded successfully');
                  } else {
                    console.warn('⚠️ Health certificate upload failed:', result.error);
                  }
                  return result;
                })
                .catch(err => {
                  console.error('❌ Health certificate upload error:', err);
                })
            );
          }

          if (formData.vaccinationRecord) {
            console.log('🔵 [ADD CATTLE] Uploading vaccination record...');
            uploadPromises.push(
              gauShalaApi.cattle.uploadDocument(cattleId, formData.vaccinationRecord, 'vaccination_record')
                .then(result => {
                  if (result.success) {
                    console.log('✅ Vaccination record uploaded successfully');
                  } else {
                    console.warn('⚠️ Vaccination record upload failed:', result.error);
                  }
                  return result;
                })
                .catch(err => {
                  console.error('❌ Vaccination record upload error:', err);
                })
            );
          }

          if (formData.purchaseDocument) {
            console.log('🔵 [ADD CATTLE] Uploading purchase document...');
            uploadPromises.push(
              gauShalaApi.cattle.uploadDocument(cattleId, formData.purchaseDocument, 'purchase_document')
                .then(result => {
                  if (result.success) {
                    console.log('✅ Purchase document uploaded successfully');
                  } else {
                    console.warn('⚠️ Purchase document upload failed:', result.error);
                  }
                  return result;
                })
                .catch(err => {
                  console.error('❌ Purchase document upload error:', err);
                })
            );
          }

          // Wait for all uploads but don't fail registration if they fail
          if (uploadPromises.length > 0) {
            console.log('🔵 [ADD CATTLE] Waiting for document uploads...');
            await Promise.allSettled(uploadPromises);
            console.log('✅ Document uploads completed');
          }
        } catch (docError) {
          // Log but don't fail - documents are optional
          console.warn('⚠️ Document upload error (non-blocking):', docError);
        }

        console.log('✅ [ADD CATTLE] Cattle registered successfully with ID:', cattleId);
        setMessage({
          type: 'success',
          text: 'Cattle registered successfully!'
        });

        // Wait a bit to ensure backend has processed all uploads before navigating
        // This ensures the photo and other documents are available when viewing the cattle
        setTimeout(() => {
          console.log('🔵 [ADD CATTLE] Navigating to cattle list with refresh flag');
          navigate('/gaushala/cattle', { state: { shouldRefresh: true } });
        }, 3000);
      } else {
        const errorMsg = response.error || 'Failed to create cattle';
        console.error('❌ [ADD CATTLE] Registration failed:', errorMsg);
        throw new Error(errorMsg);
      }
    } catch (error) {
      // Extract meaningful error message from various error formats
      let errorMessage = 'Failed to register cattle. Please try again.';

      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      }

      console.error('Cattle registration error:', error);
      setMessage({
        type: 'error',
        text: errorMessage
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/gaushala/cattle');
  };

  const sections = [
    {
      id: 'basic',
      title: 'Basic Identification',
      icon: User,
      color: 'from-blue-500 to-indigo-600',
      progress: 0
    },
    {
      id: 'physical',
      title: 'Physical Characteristics',
      icon: Ruler,
      color: 'from-purple-500 to-violet-600',
      progress: 0
    },
    {
      id: 'health',
      title: 'Health & Medical',
      icon: Activity,
      color: 'from-red-500 to-pink-600',
      progress: 0
    },
    {
      id: 'reproductive',
      title: 'Reproductive Details',
      icon: Baby,
      color: 'from-pink-500 to-rose-600',
      progress: 0
    },
    {
      id: 'origin',
      title: 'Origin & Ownership',
      icon: Globe,
      color: 'from-emerald-500 to-teal-600',
      progress: 0
    },
    {
      id: 'shelter',
      title: 'Shelter & Feeding',
      icon: Settings,
      color: 'from-amber-500 to-yellow-600',
      progress: 0
    }
  ];


  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <button
            onClick={handleCancel}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span className="font-medium">Back</span>
          </button>

          <div className="h-8 w-px bg-gray-300"></div>

          <div className="flex items-center gap-3">
            <div className="h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-2xl">🐄</span>
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Register New Cattle</h2>
              <p className="text-gray-600">Complete cattle registration with comprehensive details</p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={handleCancel}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="cattle-form"
            disabled={isLoading}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <Save className="h-4 w-4" />
            {isLoading ? 'Saving...' : 'Save Cattle'}
          </button>
        </div>
      </div>

      {/* Progress Indicator */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
        <div className="flex items-center justify-between overflow-x-auto">
          {sections.map((section, index) => {
            const Icon = section.icon;
            return (
              <div key={section.id} className="flex items-center flex-shrink-0">
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-blue-100 text-blue-600">
                  <Icon className="h-4 w-4" />
                </div>
                <div className="ml-2">
                  <p className="text-xs font-medium text-gray-700">{section.title}</p>
                </div>
                {index < sections.length - 1 && (
                  <div className="w-8 h-px bg-gray-300 mx-3"></div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Message Alert */}
      {message && (
        <div className={`p-4 rounded-lg border-l-4 ${
          message.type === 'success'
            ? 'bg-green-50 border-green-400 text-green-800'
            : message.type === 'warning'
            ? 'bg-yellow-50 border-yellow-400 text-yellow-800'
            : 'bg-red-50 border-red-400 text-red-800'
        }`}>
          <div className="flex items-center gap-3">
            {message.type === 'success' ? (
              <CheckCircle className="h-5 w-5" />
            ) : message.type === 'warning' ? (
              <Clock className="h-5 w-5" />
            ) : (
              <AlertCircle className="h-5 w-5" />
            )}
            <span className="font-medium">{message.text}</span>
          </div>
        </div>
      )}
      <form id="cattle-form" onSubmit={handleSubmit} className="space-y-6">
        {/* Main Grid Layout */}
        <div className="grid grid-cols-1 xl:grid-cols-4 gap-6">

          {/* Left Section - 3 columns */}
          <div className="xl:col-span-3 space-y-6">

            {/* Basic Identification */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-white" />
                </div>
                🐄 Basic Identification
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Unique Animal ID <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.uniqueAnimalId.length}/{VALIDATION_RULES.uniqueAnimalId.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.uniqueAnimalId}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.uniqueAnimalId.max) {
                          handleInputChange('uniqueAnimalId', e.target.value);
                        }
                      }}
                      placeholder="Enter unique animal ID"
                      maxLength={VALIDATION_RULES.uniqueAnimalId.max}
                      className={`w-full px-4 py-3.5 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                        errors.uniqueAnimalId ? 'border-red-300 bg-red-50' : 'border-gray-200'
                      }`}
                      required
                    />
                    {errors.uniqueAnimalId && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <span>⚠️</span> {errors.uniqueAnimalId}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Name (Optional)
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.name.length}/{VALIDATION_RULES.name.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.name.max) {
                          handleInputChange('name', e.target.value);
                        }
                      }}
                      placeholder="Enter cattle name"
                      maxLength={VALIDATION_RULES.name.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <SelectField
                    label="Breed"
                    value={formData.breedId}
                    onChange={(value: string) => handleInputChange('breedId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading breeds...' : 'Select Breed'}
                    required
                    error={errors.breedId}
                    options={breeds.filter(breed => breed && breed.id && breed.name).map(breed => ({
                      value: breed.id.toString(),
                      label: breed.name
                    })).sort((a, b) => a.label.localeCompare(b.label))}
                  />

                  <SelectField
                    label="Species"
                    value={formData.speciesId}
                    onChange={(value: string) => handleInputChange('speciesId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading species...' : 'Select Species'}
                    required
                    error={errors.speciesId}
                    options={species.filter(s => s && s.id && s.name).map(s => ({
                      value: s.id.toString(),
                      label: s.name
                    })).sort((a, b) => a.label.localeCompare(b.label))}
                  />

                  <SelectField
                    label="Gender"
                    value={formData.genderId}
                    onChange={(value: string) => handleInputChange('genderId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading genders...' : 'Select Gender'}
                    required
                    error={errors.genderId}
                    options={genders.filter(gender => gender && gender.id && gender.name).map(gender => ({
                      value: gender.id.toString(),
                      label: gender.name
                    })).sort((a, b) => a.label.localeCompare(b.label))}
                  />

                  <SelectField
                    label="Color"
                    value={formData.colorId}
                    onChange={(value: string) => handleInputChange('colorId', parseInt(value) || 0)}
                    placeholder={masterDataLoading ? 'Loading colors...' : 'Select Color'}
                    error={errors.colorId}
                    options={colors.filter(color => color && color.id && color.name).map(color => ({
                      value: color.id.toString(),
                      label: color.name
                    })).sort((a, b) => a.label.localeCompare(b.label))}
                  />

                  <InputField
                    label="Age (Years)"
                    value={formData.ageYears}
                    onChange={(value: string) => handleInputChange('ageYears', parseInt(value) || 0)}
                    type="number"
                    min="0"
                    max="30"
                    placeholder="Enter age in years"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Date of Entry <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      value={formData.dateOfEntry}
                      onChange={(e) => {
                        handleInputChange('dateOfEntry', e.target.value);
                      }}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                    <p className="text-xs text-gray-500 mt-1">📅 Select any past or future date</p>
                  </div>
                </div>
              </div>


            {/* Physical Characteristics */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-purple-500 to-violet-600 rounded-full flex items-center justify-center">
                  <Ruler className="h-5 w-5 text-white" />
                </div>
                📏 Physical Characteristics
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <InputField
                    label="Weight (kg)"
                    value={formData.weight}
                    onChange={(value: string) => handleInputChange('weight', value)}
                    type="number"
                    step="0.1"
                    min="0"
                    max="2000"
                    placeholder="Weight in kilograms"
                  />

                  <InputField
                    label="Height (cm)"
                    value={formData.height}
                    onChange={(value: string) => handleInputChange('height', value)}
                    type="number"
                    min="0"
                    max="300"
                    placeholder="Height in centimeters"
                  />

                  <SelectField
                    label="Horn Status"
                    value={formData.hornStatus}
                    onChange={(value: string) => handleInputChange('hornStatus', value)}
                    placeholder="Select Status"
                    options={[
                      { value: 'horned', label: 'Horned' },
                      { value: 'dehorned', label: 'Dehorned' },
                      { value: 'polled', label: 'Polled (Naturally hornless)' }
                    ].sort((a, b) => a.label.localeCompare(b.label))}
                  />

                  <div className="lg:col-span-2">
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      RFID Tag Number <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.rfidTagNo.length}/{VALIDATION_RULES.rfidTagNo.max})</span>
                    </label>
                    <div className="flex gap-3">
                      <input
                        type="text"
                        value={formData.rfidTagNo}
                        onChange={(e) => {
                          if (e.target.value.length <= VALIDATION_RULES.rfidTagNo.max) {
                            handleInputChange('rfidTagNo', e.target.value);
                          }
                        }}
                        placeholder="Scan or enter RFID tag"
                        maxLength={VALIDATION_RULES.rfidTagNo.max}
                        className={`flex-1 px-4 py-3.5 bg-white border-2 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 ${
                          errors.rfidTagNo ? 'border-red-300 bg-red-50' : 'border-gray-200'
                        }`}
                        required
                      />
                      <button
                        type="button"
                        onClick={handleScanRfid}
                        disabled={isScanning}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                      >
                        <Scan className="h-5 w-5" />
                        {isScanning ? 'Scanning...' : 'Scan'}
                      </button>
                    </div>
                    {errors.rfidTagNo && (
                      <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <span>⚠️</span> {errors.rfidTagNo}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Ear Tag Number
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.earTagNumber.length}/{VALIDATION_RULES.earTagNumber.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.earTagNumber}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.earTagNumber.max) {
                          handleInputChange('earTagNumber', e.target.value);
                        }
                      }}
                      placeholder="Ear tag number"
                      maxLength={VALIDATION_RULES.earTagNumber.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Microchip Number
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.microchipNumber.length}/{VALIDATION_RULES.microchipNumber.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.microchipNumber}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.microchipNumber.max) {
                          handleInputChange('microchipNumber', e.target.value);
                        }
                      }}
                      placeholder="Microchip number"
                      maxLength={VALIDATION_RULES.microchipNumber.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>
                </div>
              </div>

            {/* Health & Medical Records */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-red-500 to-pink-600 rounded-full flex items-center justify-center">
                  <Activity className="h-5 w-5 text-white" />
                </div>
                🩺 Health & Medical Records
              </h2>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Vaccination Status
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.vaccinationStatus.length}/{VALIDATION_RULES.vaccinationStatus.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.vaccinationStatus}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.vaccinationStatus.max) {
                          handleInputChange('vaccinationStatus', e.target.value);
                        }
                      }}
                      placeholder="e.g., FMD, HS completed"
                      maxLength={VALIDATION_RULES.vaccinationStatus.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Disability / Injury
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.disability.length}/{VALIDATION_RULES.disability.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.disability}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.disability.max) {
                          handleInputChange('disability', e.target.value);
                        }
                      }}
                      placeholder="Any disabilities or injuries"
                      maxLength={VALIDATION_RULES.disability.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Veterinarian Name
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.veterinarianName.length}/{VALIDATION_RULES.veterinarianName.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.veterinarianName}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.veterinarianName.max) {
                          handleInputChange('veterinarianName', e.target.value);
                        }
                      }}
                      placeholder="Name of attending veterinarian"
                      maxLength={VALIDATION_RULES.veterinarianName.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Veterinarian Contact
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.veterinarianContact.length}/{VALIDATION_RULES.veterinarianContact.max})</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.veterinarianContact}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.veterinarianContact.max) {
                          handleInputChange('veterinarianContact', e.target.value);
                        }
                      }}
                      placeholder="Phone number"
                      maxLength={VALIDATION_RULES.veterinarianContact.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <InputField
                    label="Last Health Check-up"
                    value={formData.lastHealthCheckup}
                    onChange={(value: string) => handleInputChange('lastHealthCheckup', value)}
                    type="date"
                  />

                  <div>
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Deworming Schedule
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.dewormingSchedule.length}/{VALIDATION_RULES.dewormingSchedule.max})</span>
                    </label>
                    <input
                      type="text"
                      value={formData.dewormingSchedule}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.dewormingSchedule.max) {
                          handleInputChange('dewormingSchedule', e.target.value);
                        }
                      }}
                      placeholder="e.g., Every 6 months"
                      maxLength={VALIDATION_RULES.dewormingSchedule.max}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                    />
                  </div>

                  <div className="lg:col-span-3">
                    <label className="block text-sm font-medium text-gray-800 mb-2">
                      Medical History
                      <span className="text-xs text-gray-500 font-normal ml-2">({formData.medicalHistory.length}/{VALIDATION_RULES.medicalHistory.max})</span>
                    </label>
                    <textarea
                      value={formData.medicalHistory}
                      onChange={(e) => {
                        if (e.target.value.length <= VALIDATION_RULES.medicalHistory.max) {
                          handleInputChange('medicalHistory', e.target.value);
                        }
                      }}
                      placeholder="Previous medical treatments, surgeries, etc."
                      maxLength={VALIDATION_RULES.medicalHistory.max}
                      rows={4}
                      className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300 resize-none"
                    />
                  </div>
                </div>
              </div>

              {/* Additional Sections Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* Reproductive Details (Female only) */}
                {/* Note: Check if genderId matches female gender ID from master data */}
                {genders.find(g => g.name && g.name.toLowerCase() === 'female' && g.id === formData.genderId) && (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                    <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                      <div className="h-10 w-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center">
                        <Baby className="h-5 w-5 text-white" />
                      </div>
                      🤱 Reproductive Details
                    </h2>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <SelectField
                        label="Milking Status"
                        value={formData.milkingStatus}
                        onChange={(value: string) => handleInputChange('milkingStatus', value)}
                        placeholder="Select Status"
                        options={[
                          { value: 'lactating', label: 'Lactating' },
                          { value: 'dry', label: 'Dry' },
                          { value: 'pregnant', label: 'Pregnant' },
                          { value: 'not_breeding', label: 'Not for Breeding' }
                        ].sort((a, b) => a.label.localeCompare(b.label))}
                      />

                      <InputField
                        label="Milk Yield Per Day (Liters)"
                        value={formData.milkYieldPerDay}
                        onChange={(value: string) => handleInputChange('milkYieldPerDay', value)}
                        type="number"
                        step="0.1"
                        min="0"
                        max="100"
                        placeholder="Daily milk yield"
                      />

                      <InputField
                        label="Number of Calves"
                        value={formData.numberOfCalves}
                        onChange={(value: string) => handleInputChange('numberOfCalves', value)}
                        type="number"
                        min="0"
                        max="50"
                        placeholder="Total calves born"
                      />

                      <InputField
                        label="Lactation Number"
                        value={formData.lactationNumber}
                        onChange={(value: string) => handleInputChange('lactationNumber', value)}
                        type="number"
                        min="0"
                        max="20"
                        placeholder="Current lactation number"
                      />

                      <InputField
                        label="Last Calving Date"
                        value={formData.lastCalvingDate}
                        onChange={(value: string) => handleInputChange('lastCalvingDate', value)}
                        type="date"
                      />

                      <SelectField
                        label="Pregnancy Status"
                        value={formData.pregnancyStatus}
                        onChange={(value: string) => handleInputChange('pregnancyStatus', value)}
                        placeholder="Select Status"
                        options={[
                          { value: 'pregnant', label: 'Pregnant' },
                          { value: 'not_pregnant', label: 'Not Pregnant' },
                          { value: 'unknown', label: 'Unknown' }
                        ].sort((a, b) => a.label.localeCompare(b.label))}
                      />
                    </div>
                  </div>
                )}

                {/* Origin & Ownership */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
                      <Globe className="h-5 w-5 text-white" />
                    </div>
                    🌍 Origin & Ownership
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <SelectField
                      label="Source of Acquisition"
                      value={formData.sourceOfAcquisition}
                      onChange={(value: string) => handleInputChange('sourceOfAcquisition', value)}
                      placeholder="Select Source"
                      options={[
                        { value: 'purchase', label: 'Purchase' },
                        { value: 'donation', label: 'Donation' },
                        { value: 'rescue', label: 'Rescue' },
                        { value: 'transfer', label: 'Transfer' },
                        { value: 'born_here', label: 'Born Here' }
                      ].sort((a, b) => a.label.localeCompare(b.label))}
                    />

                    <div>
                      <label className="block text-sm font-medium text-gray-800 mb-2">
                        Previous Owner
                        <span className="text-xs text-gray-500 font-normal ml-2">({formData.previousOwner.length}/{VALIDATION_RULES.previousOwner.max})</span>
                      </label>
                      <input
                        type="text"
                        value={formData.previousOwner}
                        onChange={(e) => {
                          if (e.target.value.length <= VALIDATION_RULES.previousOwner.max) {
                            handleInputChange('previousOwner', e.target.value);
                          }
                        }}
                        placeholder="Name of previous owner"
                        maxLength={VALIDATION_RULES.previousOwner.max}
                        className="w-full px-4 py-3.5 bg-white border-2 border-gray-200 rounded-xl transition-all duration-200 text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 hover:border-gray-300"
                      />
                    </div>

                    <InputField
                      label="Date of Acquisition"
                      value={formData.dateOfAcquisition}
                      onChange={(value: string) => handleInputChange('dateOfAcquisition', value)}
                      type="date"
                    />

                    <SelectField
                      label="Ownership Status"
                      value={formData.ownershipStatus}
                      onChange={(value: string) => handleInputChange('ownershipStatus', value)}
                      placeholder="Select Status"
                      options={[
                        { value: 'owned', label: 'Owned by Gaushala' },
                        { value: 'fostered', label: 'Fostered' },
                        { value: 'temporary', label: 'Temporary Care' },
                        { value: 'sponsored', label: 'Sponsored' }
                      ].sort((a, b) => a.label.localeCompare(b.label))}
                    />
                  </div>
                </div>

                {/* Shelter & Feeding */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
                  <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                    <div className="h-10 w-10 bg-gradient-to-br from-amber-500 to-yellow-600 rounded-full flex items-center justify-center">
                      <Settings className="h-5 w-5 text-white" />
                    </div>
                    🏠 Shelter & Feeding
                  </h2>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <MultiSelectField
                      label="Shed Assignment"
                      value={formData.shedNumber}
                      onChange={(value: any) => handleInputChange('shedNumber', value)}
                      placeholder={masterDataLoading ? 'Loading sheds...' : sheds.length === 0 ? 'No sheds available' : 'Select Sheds (multiple)'}
                      options={sheds.map(shed => ({
                        value: shed.shedNumber,
                        label: `${shed.shedName} (${shed.currentOccupancy || 0}/${shed.capacity || 0} occupied)`
                      })).sort((a, b) => a.label.localeCompare(b.label))}
                    />

                    <MultiSelectField
                      label="Type of Feed"
                      value={formData.typeOfFeed}
                      onChange={(value: any) => handleInputChange('typeOfFeed', value)}
                      placeholder="Select Feed Types (multiple)"
                      options={[
                        { value: 'grass', label: 'Grass' },
                        { value: 'hay', label: 'Hay' },
                        { value: 'silage', label: 'Silage' },
                        { value: 'concentrate', label: 'Concentrate' },
                        { value: 'mixed', label: 'Mixed Feed' }
                      ].sort((a, b) => a.label.localeCompare(b.label))}
                    />

                    <div className="md:col-span-2">
                      <TextAreaField
                        label="Feeding Schedule"
                        value={formData.feedingSchedule}
                        onChange={(value: string) => handleInputChange('feedingSchedule', value)}
                        placeholder="Feeding times and quantities"
                        maxLength={300}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

          {/* Right Sidebar */}
          <div className="xl:col-span-1 space-y-6">

            {/* Supporting Documents */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 sticky top-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-3">
                <div className="h-10 w-10 bg-gradient-to-br from-slate-500 to-gray-600 rounded-full flex items-center justify-center">
                  <FileText className="h-5 w-5 text-white" />
                </div>
                📎 Documents
              </h2>

                <div className="space-y-6">
                  <FileUploadField
                    label="Photo"
                    value={formData.photoFile}
                    onChange={(value: File | null) => handleInputChange('photoFile', value)}
                    accept="image/*"
                  />

                  <FileUploadField
                    label="Health Certificate"
                    value={formData.healthCertificate}
                    onChange={(value: File | null) => handleInputChange('healthCertificate', value)}
                    accept=".pdf"
                  />

                  <FileUploadField
                    label="Vaccination Record"
                    value={formData.vaccinationRecord}
                    onChange={(value: File | null) => handleInputChange('vaccinationRecord', value)}
                    accept=".pdf"
                  />

                  <FileUploadField
                    label="Purchase/Donation Document"
                    value={formData.purchaseDocument}
                    onChange={(value: File | null) => handleInputChange('purchaseDocument', value)}
                    accept=".pdf"
                  />
                </div>

                {/* Quick Stats */}
                <div className="mt-8 pt-6 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-4">Form Progress</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Required Fields</span>
                      <span className="font-medium text-gray-900">
                        {Object.keys(errors).length === 0 ? '✓ Complete' : `${Object.keys(errors).length} missing`}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Documents</span>
                      <span className="font-medium text-gray-900">
                        {[formData.photoFile, formData.healthCertificate, formData.vaccinationRecord, formData.purchaseDocument].filter(Boolean).length}/4
                      </span>
                    </div>
                  </div>
                </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}